import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";
import {
    computeTerrainField,
    computeFieldMinMax,
    computeTopoThresholds,
    type TerrainParams,
} from "../../utils/topographicMap";
import { marchingSquares } from "../../utils/marchingSquares";
import { interpolateColor } from "../../utils/svgUtils";

export interface TopographicContourLevel {
    paths: string[];
    level: number;
    threshold: number;
}

interface TopographicMapState {
    canvasW: number;
    canvasH: number;
    sampleStep: number;
    seed: string;
    noiseScale: number;
    octaves: number;
    contourCount: number;
    indexEvery: number;
    indexStrokeWidthFactor: number;
    fillColor: string;
    strokeColor: string;
    indexStrokeColor: string;
    bgColor: string;
    strokeWidth: number;
    fillShapes: boolean;
    /** When true, fill color interpolates from fillColor (low) to fillColorHigh by threshold. */
    fillByAltitude: boolean;
    fillColorHigh: string;
    setCanvasW: (v: number) => void;
    setCanvasH: (v: number) => void;
    setSampleStep: (v: number) => void;
    setSeed: (v: string) => void;
    setNoiseScale: (v: number) => void;
    setOctaves: (v: number) => void;
    setContourCount: (v: number) => void;
    setIndexEvery: (v: number) => void;
    setIndexStrokeWidthFactor: (v: number) => void;
    setFillColor: (v: string) => void;
    setStrokeColor: (v: string) => void;
    setIndexStrokeColor: (v: string) => void;
    setBgColor: (v: string) => void;
    setStrokeWidth: (v: number) => void;
    setFillShapes: (v: boolean) => void;
    setFillByAltitude: (v: boolean) => void;
    setFillColorHigh: (v: string) => void;
    fillColorForLevel: (threshold: number, level: number) => string;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
    randomizeParameters: () => void;
    viewBoxAttr: string;
    contourData: TopographicContourLevel[];
    fieldRange: { minV: number; maxV: number } | null;
    isIndexLevel: (level: number) => boolean;
    canExport: boolean;
    buildExportSvgString: () => string;
    handleDownload: () => void;
}

const TopographicMapContext = createContext<TopographicMapState | null>(null);

const DEFAULT_TOPOGRAPHIC_MAP_PRESET: Record<string, unknown> = {
    canvasW: 800,
    canvasH: 600,
    sampleStep: 4,
    seed: "topo",
    noiseScale: 0.003,
    octaves: 5,
    contourCount: 18,
    indexEvery: 5,
    indexStrokeWidthFactor: 1.75,
    fillColor: "#6b8e6b",
    strokeColor: "#4a5d4a",
    indexStrokeColor: "#2d3d2d",
    bgColor: "#f4f1e8",
    strokeWidth: 0.6,
    fillShapes: false,
    fillByAltitude: false,
    fillColorHigh: "#c9b896",
};

export function useTopographicMap() {
    const ctx = useContext(TopographicMapContext);
    if (!ctx)
        throw new Error("useTopographicMap must be used within TopographicMapProvider");
    return ctx;
}

function seededRNG(seed: string): () => number {
    let s = [...seed].reduce((a, c) => a + c.charCodeAt(0), 0) || 1;
    return () => {
        s = (s * 9301 + 49297) % 233280;
        return s / 233280;
    };
}

export function TopographicMapProvider({ children }: { children: React.ReactNode }) {
    const [canvasW, setCanvasW] = useState(800);
    const [canvasH, setCanvasH] = useState(600);
    const [sampleStep, setSampleStep] = useState(4);
    const [seed, setSeed] = useState("topo");
    const [noiseScale, setNoiseScale] = useState(0.003);
    const [octaves, setOctaves] = useState(5);
    const [contourCount, setContourCount] = useState(18);
    const [indexEvery, setIndexEvery] = useState(5);
    const [indexStrokeWidthFactor, setIndexStrokeWidthFactor] = useState(1.75);
    const [fillColor, setFillColor] = useState("#6b8e6b");
    const [strokeColor, setStrokeColor] = useState("#4a5d4a");
    const [indexStrokeColor, setIndexStrokeColor] = useState("#2d3d2d");
    const [bgColor, setBgColor] = useState("#f4f1e8");
    const [strokeWidth, setStrokeWidth] = useState(0.6);
    const [fillShapes, setFillShapes] = useState(false);
    const [fillByAltitude, setFillByAltitude] = useState(false);
    const [fillColorHigh, setFillColorHigh] = useState("#c9b896");

    const viewBoxAttr = `0 0 ${canvasW} ${canvasH}`;

    const fieldParams = useMemo<TerrainParams>(
        () => ({
            canvasW,
            canvasH,
            sampleStep,
            seed,
            noiseScale,
            octaves,
        }),
        [canvasW, canvasH, sampleStep, seed, noiseScale, octaves],
    );

    const scalarField = useMemo(
        () => computeTerrainField(fieldParams),
        [fieldParams],
    );

    const fieldRange = useMemo(
        () => computeFieldMinMax(scalarField),
        [scalarField],
    );

    const thresholds = useMemo(() => {
        if (!fieldRange) return [] as number[];
        return computeTopoThresholds(
            fieldRange.minV,
            fieldRange.maxV,
            contourCount,
        );
    }, [fieldRange, contourCount]);

    const contourData = useMemo<TopographicContourLevel[]>(() => {
        if (thresholds.length === 0) return [];
        const levels: TopographicContourLevel[] = [];
        for (let i = 0; i < thresholds.length; i++) {
            const threshold = thresholds[i]!;
            const paths = marchingSquares(scalarField, sampleStep, threshold);
            levels.push({ paths, level: i, threshold });
        }
        return levels;
    }, [scalarField, sampleStep, thresholds]);

    const isIndexLevel = useCallback(
        (level: number) => {
            if (indexEvery < 2) return false;
            return (level + 1) % indexEvery === 0;
        },
        [indexEvery],
    );

    const elevationT = useCallback(
        (threshold: number, level: number): number => {
            if (fieldRange && fieldRange.maxV > fieldRange.minV) {
                const t =
                    (threshold - fieldRange.minV) /
                    (fieldRange.maxV - fieldRange.minV);
                return Math.max(0, Math.min(1, t));
            }
            if (contourCount <= 1) return 0;
            return Math.max(0, Math.min(1, level / (contourCount - 1)));
        },
        [fieldRange, contourCount],
    );

    const fillColorForLevel = useCallback(
        (threshold: number, level: number): string => {
            if (fillByAltitude) {
                return interpolateColor(
                    fillColor,
                    fillColorHigh,
                    elevationT(threshold, level),
                );
            }
            return fillColor;
        },
        [fillByAltitude, fillColor, fillColorHigh, elevationT],
    );

    const canExport = contourData.some((l) => l.paths.length > 0);

    const buildExportSvgString = useCallback((): string => {
        const contourPaths = contourData
            .map(({ paths, level, threshold }) => {
                const idx = isIndexLevel(level);
                const sw = idx
                    ? strokeWidth * indexStrokeWidthFactor
                    : strokeWidth;
                const sc = idx ? indexStrokeColor : strokeColor;
                const fillHex = fillShapes
                    ? fillColorForLevel(threshold, level)
                    : "none";
                const opacity = fillShapes
                    ? fillByAltitude
                        ? "1"
                        : ((level + 1) / contourCount).toFixed(3)
                    : "1";
                return paths
                    .map(
                        (d) =>
                            `<path d="${d}" fill="${fillHex}" fill-opacity="${opacity}" stroke="${sc}" stroke-width="${sw}" />`,
                    )
                    .join("\n    ");
            })
            .join("\n    ");

        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBoxAttr}">
  <rect width="${canvasW}" height="${canvasH}" fill="${bgColor}" />
  <g class="topo-contours">
    ${contourPaths}
  </g>
</svg>`;
    }, [
        contourData,
        contourCount,
        fillShapes,
        fillByAltitude,
        fillColor,
        fillColorForLevel,
        strokeColor,
        indexStrokeColor,
        strokeWidth,
        indexStrokeWidthFactor,
        bgColor,
        canvasW,
        canvasH,
        viewBoxAttr,
        isIndexLevel,
    ]);

    const handleDownload = useCallback(() => {
        if (!canExport) return;
        const svgString = buildExportSvgString();
        const blob = new Blob([svgString], {
            type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `topographic-${seed}-${Date.now()}.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [canExport, buildExportSvgString, seed]);


    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            canvasW, canvasH, sampleStep, seed, noiseScale, octaves,
            contourCount, indexEvery, indexStrokeWidthFactor,
            fillColor, strokeColor, indexStrokeColor, bgColor, strokeWidth,
            fillShapes, fillByAltitude, fillColorHigh,
        }),
        [
            canvasW, canvasH, sampleStep, seed, noiseScale, octaves,
            contourCount, indexEvery, indexStrokeWidthFactor,
            fillColor, strokeColor, indexStrokeColor, bgColor, strokeWidth,
            fillShapes, fillByAltitude, fillColorHigh,
        ],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (values.canvasW != null) setCanvasW(values.canvasW as number);
        if (values.canvasH != null) setCanvasH(values.canvasH as number);
        if (values.sampleStep != null) setSampleStep(values.sampleStep as number);
        if (values.seed != null) setSeed(values.seed as string);
        if (values.noiseScale != null) setNoiseScale(values.noiseScale as number);
        if (values.octaves != null) setOctaves(values.octaves as number);
        if (values.contourCount != null) setContourCount(values.contourCount as number);
        if (values.indexEvery != null) setIndexEvery(values.indexEvery as number);
        if (values.indexStrokeWidthFactor != null) setIndexStrokeWidthFactor(values.indexStrokeWidthFactor as number);
        if (values.fillColor != null) setFillColor(values.fillColor as string);
        if (values.strokeColor != null) setStrokeColor(values.strokeColor as string);
        if (values.indexStrokeColor != null) setIndexStrokeColor(values.indexStrokeColor as string);
        if (values.bgColor != null) setBgColor(values.bgColor as string);
        if (values.strokeWidth != null) setStrokeWidth(values.strokeWidth as number);
        if (values.fillShapes != null) setFillShapes(values.fillShapes as boolean);
        if (values.fillByAltitude != null) setFillByAltitude(values.fillByAltitude as boolean);
        if (values.fillColorHigh != null) setFillColorHigh(values.fillColorHigh as string);
    }, []);

    const { resetParameters } = useToolParamPersistence({
        toolId: "topographic-map",
        defaultPreset: DEFAULT_TOPOGRAPHIC_MAP_PRESET,
        getPresetValues,
        applyPreset,
    });

    const randomizeParameters = useCallback(() => {
        const rng = seededRNG(Date.now().toString());
        setSeed(Math.floor(rng() * 9000 + 1000).toString());
        setNoiseScale(parseFloat((rng() * 0.005 + 0.001).toFixed(4)));
        setOctaves(Math.floor(rng() * 5) + 3);
        setContourCount(Math.floor(rng() * 22) + 10);
        setIndexEvery(Math.floor(rng() * 4) + 3);
    }, []);

    const value: TopographicMapState = {
        canvasW,
        canvasH,
        sampleStep,
        seed,
        noiseScale,
        octaves,
        contourCount,
        indexEvery,
        indexStrokeWidthFactor,
        fillColor,
        strokeColor,
        indexStrokeColor,
        bgColor,
        strokeWidth,
        fillShapes,
        fillByAltitude,
        fillColorHigh,
        fillColorForLevel,
        setCanvasW,
        setCanvasH,
        setSampleStep,
        setSeed,
        setNoiseScale,
        setOctaves,
        setContourCount,
        setIndexEvery,
        setIndexStrokeWidthFactor,
        setFillColor,
        setStrokeColor,
        setIndexStrokeColor,
        setBgColor,
        setStrokeWidth,
        setFillShapes,
        setFillByAltitude,
        setFillColorHigh,
        applyPreset,
        getPresetValues,
        resetParameters,
        randomizeParameters,
        viewBoxAttr,
        contourData,
        fieldRange,
        isIndexLevel,
        canExport,
        buildExportSvgString,
        handleDownload,
    };

    return (
        <TopographicMapContext.Provider value={value}>
            {children}
        </TopographicMapContext.Provider>
    );
}
